import Echo from 'laravel-echo';
import Pusher from 'pusher-js';

// Configuration de Laravel Echo pour Reverb
window.Pusher = Pusher;

// Intercepter les erreurs WebSocket de Pusher avant qu'elles ne soient loguées
// Cela évite de polluer la console avec des erreurs si Reverb n'est pas démarré
const originalConsoleError = console.error;
const originalConsoleWarn = console.warn;

// Intercepter les erreurs liées à WebSocket/Pusher
const filteredConsoleError = (...args) => {
  const message = args.join(' ');
  // Ignorer les erreurs WebSocket de Pusher
  if (
    message.includes('WebSocket connection') ||
    message.includes('failed: Could not connect') ||
    message.includes('pusher') ||
    message.includes('reverb')
  ) {
    // Ne rien logger - WebSocket est optionnel
    return;
  }
  originalConsoleError.apply(console, args);
};

const filteredConsoleWarn = (...args) => {
  const message = args.join(' ');
  // Ignorer les warnings WebSocket de Pusher
  if (
    message.includes('WebSocket connection') ||
    message.includes('failed: Could not connect') ||
    message.includes('pusher') ||
    message.includes('reverb')
  ) {
    // Ne rien logger - WebSocket est optionnel
    return;
  }
  originalConsoleWarn.apply(console, args);
};

// Appliquer les filtres seulement si WebSocket n'est pas explicitement activé en debug
if (process.env.REACT_APP_DEBUG_WEBSOCKET !== 'true') {
  console.error = filteredConsoleError;
  console.warn = filteredConsoleWarn;
}

// Récupérer le token depuis sessionStorage
const getToken = () => {
  try {
    const storedAuthState = sessionStorage.getItem('authLocalstorage');
    if (storedAuthState) {
      const parsedAuthState = JSON.parse(storedAuthState);
      return parsedAuthState?.token;
    }
  } catch (e) {
    originalConsoleError('Error parsing auth state:', e);
  }
  return null;
};

// Configuration Reverb
const reverbKey = process.env.REACT_APP_REVERB_APP_KEY || 'local-key';
const reverbHost = process.env.REACT_APP_REVERB_HOST || window.location.hostname;
const reverbPort = process.env.REACT_APP_REVERB_PORT || 8080;
const reverbScheme = process.env.REACT_APP_REVERB_SCHEME || 'http';
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Debug: Log configuration (seulement en développement si activé)
if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_WEBSOCKET === 'true') {
  console.log('🔧 Reverb Configuration:', {
    key: reverbKey,
    host: reverbHost,
    port: reverbPort,
    scheme: reverbScheme,
    apiUrl: apiUrl,
    envKey: process.env.REACT_APP_REVERB_APP_KEY ? '✅ Loaded' : '❌ Missing'
  });
}

// Créer l'instance Echo avec gestion d'erreur
let echo;
let isWebSocketEnabled = process.env.REACT_APP_ENABLE_WEBSOCKET !== 'false';

try {
  if (isWebSocketEnabled) {
    echo = new Echo({
      broadcaster: 'reverb',
      key: reverbKey,
      wsHost: reverbHost,
      wsPort: reverbPort,
      wssPort: reverbPort,
      forceTLS: reverbScheme === 'https',
      enabledTransports: ['ws', 'wss'],
      authEndpoint: `${apiUrl}/api/broadcasting/auth`,
      auth: {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          Accept: 'application/json',
        },
      },
      // Options de reconnexion
      enabledAuth: true,
      disableStats: true,
      // Désactiver les logs d'erreur automatiques
      cluster: '',
    });
    
    // Gérer les erreurs de connexion silencieusement
    if (echo.connector && echo.connector.pusher && echo.connector.pusher.connection) {
      // Intercepter les erreurs de connexion pour éviter les logs dans la console
      const connection = echo.connector.pusher.connection;
      
      connection.bind('error', (err) => {
        // Ne rien logger - WebSocket est optionnel
        // Les erreurs de connexion sont normales si Reverb n'est pas démarré
      });
      
      connection.bind('connected', () => {
        if (process.env.NODE_ENV === 'development' && process.env.REACT_APP_DEBUG_WEBSOCKET === 'true') {
          console.log('✅ WebSocket connecté (Laravel Reverb)');
        }
      });
      
      connection.bind('disconnected', () => {
        // Ne rien logger - déconnexion normale
      });
      
      // Supprimer les logs d'erreur de Pusher en interceptant les erreurs réseau
      connection.bind('state_change', (state) => {
        // Ne logger que les changements importants
        if (state === 'connected') {
          // Déjà géré par le bind 'connected'
        }
      });
      
      // Désactiver les logs d'erreur de Pusher en interceptant les erreurs réseau
      const originalLog = connection.log;
      if (originalLog) {
        connection.log = function(message) {
          // Ne logger que les messages non-erreur
          if (message && !message.includes('failed') && !message.includes('error')) {
            // originalLog.call(this, message);
          }
        };
      }
    }
  } else {
    // WebSocket désactivé explicitement
    echo = createMockEcho();
  }
  
} catch (error) {
  // Créer une instance mock si l'initialisation échoue
  echo = createMockEcho();
}

// Fonction pour créer un Echo mock qui ne fait rien
function createMockEcho() {
  return {
    channel: () => ({ 
      listen: () => {}, 
      stopListening: () => {},
      unsubscribe: () => {},
    }),
    private: () => ({ 
      listen: () => {}, 
      stopListening: () => {},
      unsubscribe: () => {},
    }),
    leave: () => {},
    disconnect: () => {},
    socketId: () => null,
  };
}

export default echo;
